import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';

import { DataGridIdentifiers, DialogUI } from '../Interface';
import { formatCurrency } from '../../utils/currencyHelper';
import DataGridPremiumUI from '../Interface/DatagridPremmium/DataGridPremiumUI';
import projectService from '../../service/project.service';
import { dataGridPremiumColumns } from '../../utils/helper';
import useParam from '../../context/ParamContext';

const ProjectAllPriceDialog = ({ open, onClose, selectedValues }) => {
  const { params } = useParam();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const gridInitialState = {
    density: 'compact',
    filter: {
      filterModel: {
        items: [],
        logicOperator: 'and',
      },
    },
    sorting: {
      sortModel: [
        { field: 'itemCode', sort: 'asc' },
        { field: 'validFrom', sort: 'desc' },
      ],
    },
  };

  const getData = () => {
    if (!selectedValues.projectId) return;

    setLoading(true);

    projectService.getAllPrice(selectedValues.projectId).then((data) => {
      setRows(data);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (!open) return;
    getData();
  }, [open, selectedValues.projectId]);

  const handleDialogClose = () => {
    setRows([]);
    onClose();
  };

  const useQuantityPricing = params.PROJECT_PRICE_USE_QUANTITY === 'Y';

  const columns = useMemo(
    () =>
      dataGridPremiumColumns([
        {
          field: 'projectName',
          headerName: 'Projekt neve',
          type: 'string',
          renderCell: (data) => {
            return data.row.project.projectName;
          },
        },
        {
          field: 'itemCode',
          headerName: useQuantityPricing ? 'Megnevezés' : 'Tevékenység',
          type: 'string',
          renderCell: (data) => {
            return `${data.row.item.itemCode} - ${data.row.item.itemName}`;
          },
        },
        {
          field: 'validFrom',
          headerName: 'Kezdete',
          type: 'date',
          valueGetter: (_value, row) => new Date(row.validFrom),
          renderCell: (params) => dayjs(params.row.validFrom).format('YYYY.MM.DD'),
        },
        {
          field: 'validTo',
          headerName: 'Vége',
          type: 'date',
          valueGetter: (_value, row) => (row.validTo ? new Date(row.validTo) : null),
          renderCell: (params) => (params.row.validTo ? dayjs(params.row.validTo).format('YYYY.MM.DD') : ''),
        },
        {
          field: 'price',
          headerName: 'Ár',
          type: 'number',
          renderCell: (data) => formatCurrency(data.row.price),
        },
        {
          field: 'currencyCode',
          headerName: 'Pénznem',
          type: 'string',
        },
        {
          field: 'description',
          headerName: 'Megjegyzés',
          type: 'string',
        },
        ...(useQuantityPricing
          ? [
              {
                field: 'qty',
                headerName: 'Mennyiség',
                type: 'number',
              },
              {
                field: 'quantityUnitCode',
                headerName: 'ME',
                type: 'string',
                renderCell: (data) => {
                  return data.row.item.quantityUnit.code;
                },
              },
            ]
          : []),
      ]),
    []
  );

  return (
    <DialogUI
      closeBtn
      open={open}
      onClose={handleDialogClose}
      headerContent="Projekt árak áttekintése"
      maxWidth="xl"
      fullWidth
    >
      <Box mt={2}>
        <DataGridPremiumUI
          datagridIdentifier={DataGridIdentifiers.ProjectAllPrice}
          datagridInitialState={gridInitialState}
          height={480}
          columns={columns}
          rows={rows}
          headerFilters={false}
          getRowHeight={() => 'auto'}
          paginationAndfilterMode="client"
        />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </DialogUI>
  );
};

export default ProjectAllPriceDialog;
